import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, Wrench } from 'lucide-react';
import { Sheet, SheetContent, SheetTrigger } from './ui/sheet';
import { Button } from './ui/button';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_plumbing-heating-ns/artifacts/oxs6r0ng_Screenshot_20260215_173129_Gmail.jpg';

export const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'About', path: '/about' },
    { name: 'Services', path: '/services' },
    { name: 'Careers', path: '/careers' },
    { name: 'Contact', path: '/contact' },
  ];

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  return (
    <header
      data-testid="main-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white shadow-lg' : 'bg-white/95 backdrop-blur-sm'
      }`}
    >
      <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
            <img
              src={LOGO_URL}
              alt="Duncan's Plumbing Logo"
              className="h-12 sm:h-14 w-auto object-contain"
            />
            <span className="font-bold text-sm sm:text-lg text-[#005B96] leading-tight">
              Duncan's Plumbing Heating and Filtration
            </span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-8" data-testid="desktop-nav">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-link-${link.name.toLowerCase()}`}
                className={`font-medium transition-colors hover:text-[#005B96] ${
                  isActive(link.path) ? 'text-[#005B96]' : 'text-gray-700'
                }`}
              >
                {link.name}
              </Link>
            ))}
          </nav>

          {/* CTA Button - Desktop */}
          <a
            href="tel:902-746-0554"
            className="hidden md:flex items-center gap-2 bg-[#005B96] text-white px-6 py-3 rounded-full font-bold hover:bg-[#004a7a] transition-all shadow-lg hover:shadow-xl active:scale-95"
            data-testid="call-now-btn"
          >
            <Phone size={18} />
            <span>Call Now</span>
          </a>

          {/* Mobile Menu */}
          <Sheet open={isMobileMenuOpen} onOpenChange={setIsMobileMenuOpen}>
            <SheetTrigger asChild className="md:hidden">
              <Button variant="ghost" size="icon" data-testid="mobile-menu-btn">
                <Menu size={24} className="text-[#005B96]" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[300px] p-0">
              <div className="flex flex-col h-full bg-white">
                {/* Mobile Header */}
                <div className="p-6 border-b border-gray-100">
                  <div className="flex items-center gap-3">
                    <img
                      src={LOGO_URL}
                      alt="Duncan's Plumbing Logo"
                      className="h-12 w-auto"
                    />
                    <div>
                      <span className="font-bold text-[#005B96] block text-sm">Duncan's Plumbing Heating and Filtration</span>
                    </div>
                  </div>
                </div>

                {/* Mobile Nav Links */}
                <nav className="flex-1 p-6" data-testid="mobile-nav">
                  <ul className="space-y-4">
                    {navLinks.map((link) => (
                      <li key={link.path}>
                        <Link
                          to={link.path}
                          onClick={() => setIsMobileMenuOpen(false)}
                          data-testid={`mobile-nav-${link.name.toLowerCase()}`}
                          className={`block py-3 px-4 rounded-lg font-medium transition-colors ${
                            isActive(link.path)
                              ? 'bg-[#E6F2FA] text-[#005B96]'
                              : 'text-gray-700 hover:bg-gray-50'
                          }`}
                        >
                          {link.name}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </nav>

                {/* Mobile CTA */}
                <div className="p-6 border-t border-gray-100">
                  <a
                    href="tel:902-746-0554"
                    className="flex items-center justify-center gap-2 bg-[#005B96] text-white w-full py-4 rounded-full font-bold hover:bg-[#004a7a] transition-all"
                    data-testid="mobile-call-btn"
                  >
                    <Phone size={20} />
                    <span>902-746-0554</span>
                  </a>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};

export default Header;
